import { isPlatformServer } from '@angular/common';
import { Injectable, OnDestroy, PLATFORM_ID, inject } from '@angular/core';

import {
    CookieName,
    CookieService,
    DatadogRumService,
    LaunchDarklyContextProviderService,
    Logger,
    TrackingService,
    UserEvent,
    UserLoginEvent,
    UserLogoutEvent,
    UserPartialLoginEvent,
    UserService,
} from '@frontend/vanilla/core';
import { LDClient, LDEvaluationDetail, LDFlagChangeset, LDFlagSet, LDFlagValue, LDOptions, initialize } from 'launchdarkly-js-client-sdk';
import { BehaviorSubject, Observable, ReplaySubject, Subscription, catchError, first, map, merge, of, switchMap, take, timeout } from 'rxjs';

import { LaunchDarklyConfig } from './launch-darkly.client-config';

interface FlagValueObject {
    trackingDetails?: string;
    value: LDFlagValue;
    variationName: string;
}

/**
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class LaunchDarklyService implements OnDestroy {
    private userService = inject(UserService);
    private contextProviderService = inject(LaunchDarklyContextProviderService);
    private config = inject(LaunchDarklyConfig);
    private trackingService = inject(TrackingService);
    private cookieService = inject(CookieService);
    private logger = inject(Logger);
    private datadogRumService = inject(DatadogRumService);

    /**
     * LaunchDarkly SDK client object.
     *
     * Can be used directly to access all properties and methods provided by the SDK.
     *
     * For more information, see the [SDK Reference Guide](https://docs.launchdarkly.com/sdk/client-side/javascript).
     */
    client: LDClient;
    defaultOptions: LDOptions = {
        inspectors: [
            {
                type: 'flag-used',
                name: 'flag-used-inspector',
                method: (flagKey: string, flagDetail: LDEvaluationDetail) => this.trackFlagUsed(flagKey, flagDetail),
            },
        ],
    };
    private isServer = isPlatformServer(PLATFORM_ID);
    private flags: LDFlagSet = {};
    private userAuthenticationSubscriber: Subscription;
    private _featureFlags$ = new BehaviorSubject<LDFlagSet>({});
    private isLaunchDarklyConfigured = false;
    private readonly LD_CLIENT_INITIALIZATION_TIME_OUT = 5000;

    private _clientInitialized$ = new ReplaySubject<void>(1);
    private get clientInitialized$(): Observable<void> {
        return this._clientInitialized$.pipe(take(1));
    }

    async initialize(clientId: string) {
        this.isLaunchDarklyConfigured = true;

        if (!this.client) {
            const options = Object.assign({}, this.defaultOptions, this.config.options);
            const context = await this.contextProviderService.getContext();
            this.client = initialize(clientId, context, options);

            this.client
                .waitForInitialization()
                .then(() => {
                    this.fetchLDFlags();
                    this._clientInitialized$.next();
                    this.contextProviderService.contextChanged.subscribe(() => {
                        this.updateContext();
                    });
                    this.subscribeToUserAuthenticationEvents();
                })
                .catch((error: unknown) => {
                    this.logger.error('[LaunchDarkly] - Error while initializing SDK.', error);
                });

            this.client.on('change', async (changedFlags: LDFlagChangeset) => {
                await this.updateFlag(changedFlags);
                this.logger.info('[LaunchDarkly] - Flags changed', changedFlags);
            });

            this.client.on('error', (error: any) => {
                this.logger.error('[LaunchDarkly] - General error.', error);
            });
        }
    }

    /**
     * Emits the current value for the specified Feature flag.
     *
     * @param featureFlagKey Case sensitive feature flag name.
     * @param defaultValue Default value to be used in case flag not available in LaunchDarkly or client not initialized after 5s.
     */
    getFeatureFlagValue$(featureFlagKey: string, defaultValue?: any): Observable<any> {
        if (this.isServer) {
            return of(defaultValue);
        }
        return this.clientInitialized$.pipe(
            timeout({ first: this.LD_CLIENT_INITIALIZATION_TIME_OUT }),
            switchMap(() => {
                this.flags[featureFlagKey] = this.client?.variation(featureFlagKey, defaultValue);
                this._featureFlags$.next(this.flags);
                return this._featureFlags$.pipe(
                    map((flags: LDFlagSet) => {
                        const flagValue = flags[featureFlagKey];
                        return this.isFlagValueObjectValid(flagValue) ? flagValue.value : flagValue;
                    }),
                );
            }),
            catchError(() => of(defaultValue)),
        );
    }

    /**
     * Use this if you need the variation of a feature flag for a context, along with information about how it was calculated.
     * Note that this will only work if you have set evaluationReasons to true in LDOptions . Otherwise, the reason property of the result will be null.
     *
     * @param featureFlagKey Case sensitive feature flag name.
     * @param defaultValue Default value to be used in case flag not available in LaunchDarkly or client not initialized after 5s.
     */
    getFeatureFlagValueWithEvaluationReasons$(featureFlagKey: string, defaultValue?: any): Observable<LDEvaluationDetail> {
        if (!this.isLaunchDarklyConfigured || this.isServer) {
            return of(defaultValue);
        }

        return this.clientInitialized$.pipe(
            timeout({ first: this.LD_CLIENT_INITIALIZATION_TIME_OUT }),
            map(() => this.client?.variationDetail(featureFlagKey, defaultValue)),
            catchError(() => of(defaultValue)),
        );
    }

    /**
     * Please note that this function is not picking up the flag updates according to the changing context but returns the first value of the flag.
     *
     * @param featureFlagKey Case sensitive feature flag name.
     * @param defaultValue Default value to be used in case flag not available in LaunchDarkly or client not initialized after 5s.
     */
    getFlagOnly$(featureFlagKey: string, defaultValue?: any): Observable<any> {
        if (this.isServer) {
            return of(defaultValue);
        }
        return this.clientInitialized$.pipe(
            timeout({ first: this.LD_CLIENT_INITIALIZATION_TIME_OUT }),
            switchMap(() =>
                this._featureFlags$.pipe(
                    map((flags: LDFlagSet) => {
                        const flagValue = flags[featureFlagKey];
                        return this.isFlagValueObjectValid(flagValue) ? flagValue.value : flagValue;
                    }),
                ),
            ),
            catchError(() => of(defaultValue)),
        );
    }

    /**
     * Reloads the flags for a new context .
     * Only call this method when any of the values of the provided context has changed.
     */
    updateContext() {
        this.client?.identify(this.contextProviderService.context).then((flags: LDFlagSet) => {
            Object.keys(flags).forEach((flag: string) => {
                this.flags[flag] = flags[flag];
            });
            this._featureFlags$.next(this.flags);
        });
    }

    async ngOnDestroy() {
        this.userAuthenticationSubscriber?.unsubscribe();
        await this.client?.close();
    }

    private fetchLDFlags() {
        this.flags = this.client?.allFlags();
        this._featureFlags$.next(this.flags);
    }

    private subscribeToUserAuthenticationEvents() {
        this.userAuthenticationSubscriber = merge(
            this.userService.events.pipe(first((e: UserEvent) => e instanceof UserLoginEvent)),
            this.userService.events.pipe(first((e: UserEvent) => e instanceof UserLogoutEvent)),
            this.userService.events.pipe(first((e: UserEvent) => e instanceof UserPartialLoginEvent)),
        ).subscribe(() => this.contextProviderService.reloadContext().then(() => this.updateContext()));
    }

    private async updateFlag(changedFlags: LDFlagChangeset) {
        Object.keys(changedFlags).forEach((flag: string) => {
            this.flags[flag] = changedFlags[flag]?.current;
        });
        this._featureFlags$.next(this.flags);
    }

    private trackFlagUsed(flagKey: string, flagDetail: LDEvaluationDetail) {
        if (!flagDetail.reason?.inExperiment) {
            return;
        }

        let labelEvent: LDFlagValue;
        let trackingInfo = '';

        if (this.datadogRumService.datadogRumSdk) {
            // Track feature flag evaluation in Datadog RUM
            this.datadogRumService.datadogRumSdk.addFeatureFlagEvaluation(flagKey, flagDetail.value);
        }

        if (this.isFlagValueObjectValid(flagDetail.value)) {
            const { variationName, trackingDetails } = flagDetail.value;
            if (variationName === '') {
                this.logger.error('[LaunchDarkly] - Unable to send tracking data: variationName is empty JSON-typed variant');
                return;
            }

            labelEvent = variationName;
            trackingInfo = trackingDetails ?? '';
        } else {
            labelEvent = flagDetail.value;
        }

        // tracking only when it's part of an experiment as suggested by LD team.
        this.trackingService.triggerEvent('ld_experiment_evaluation', {
            'component.CategoryEvent': 'ld_experiment_evaluation',
            'component.ActionEvent': 'load experiment',
            'component.LabelEvent': labelEvent,
            'component.EventDetails': flagKey + trackingInfo,
        });

        // Writes cookie for WA team tracking.
        let ldExp = this.cookieService.get(CookieName.LdExperiment);
        const ldExpValue = `${flagKey}_${flagDetail.variationIndex}`;

        if (ldExp) {
            if (!ldExp.includes(ldExpValue)) {
                ldExp += `|${ldExpValue}`;
                this.cookieService.putRaw(CookieName.LdExperiment, ldExp);
            }
        } else {
            this.cookieService.putRaw(CookieName.LdExperiment, ldExpValue);
        }
    }

    /**
     * Validator function for our custom feature flag value object schema.
     * @param flagValue
     * @returns
     */
    private isFlagValueObjectValid(flagValue: unknown): flagValue is FlagValueObject {
        return (
            typeof flagValue === 'object' &&
            flagValue != null &&
            'variationName' in flagValue &&
            'value' in flagValue &&
            typeof flagValue['variationName'] === 'string' &&
            ('trackingDetails' in flagValue ? typeof flagValue['trackingDetails'] === 'string' : true)
        );
    }
}
